import { useCallback, useEffect, useState } from "react";
import type { Locale } from "../../shared/dto";
import type { AdminActivityEntry } from "../../shared/dto";
import { adminActivityPresentation } from "./AdminActivity";
import { clientPickerLabel, clientPickerOptions } from "./client-picker";
import { formatDate } from "./core";
import {
  NOTIFICATION_TYPE_NAMES,
  notificationStatusLabel,
  notificationStatusTone,
  notificationTitle,
  notificationTypeHint,
  notificationTypeLabel
} from "./notification-presentation";
import { openClientHandler } from "./OpenClient";
import { TestFilterNote } from "./TestFilterNote";
import { staffMutation, staffMutationErrorKey } from "./model";
import "./admin-selection.css";
import "./admin-notifications.css";

// ---- Servis wave 4: the notification queue (2026-08-05) ----
// The old panel was a select of failures over an unexplained "Zopakovat" and a
// drain button that 404s outside dev. It could retry a message it never showed
// and it never answered the question an operator actually has: did this reach
// the person, and if not, what did the provider say.
//
// This panel shows the queue, the attempts behind every row, the event that
// produced the message, and a delivery test that goes to the acting admin so
// the channel can be proven without touching a client.

export type NotificationStatus = "queued" | "dev_logged" | "sent" | "failed" | "skipped";
export type NotificationQueueFilter = "all" | NotificationStatus;

export const NOTIFICATION_QUEUE_FILTERS: NotificationQueueFilter[] =
  ["all", "failed", "queued", "sent", "dev_logged", "skipped"];

export interface NotificationAttempt {
  attemptedAt: string;
  provider: string;
  httpStatus: number | null;
  error: string | null;
  receipt?: string | null;
}

export interface NotificationQueueItem {
  id: string;
  clientId: string | null;
  clientName: string | null;
  isTest: boolean;
  channel: "email" | "push";
  kind: string;
  type: string;
  subject: string | null;
  recipient: string;
  /** Resolved from `recipient` when it holds a user id (push). */
  recipientName?: string | null;
  recipientEmail?: string | null;
  status: NotificationStatus;
  attemptCount: number;
  nextAttemptAt: string | null;
  sendAt: string | null;
  lastError: string | null;
  createdAt: string;
  sentAt: string | null;
  lastAttempt: NotificationAttempt | null;
}

export interface NotificationQueueCursor { rank: number; at: string; id: string }

export interface NotificationQueueResponse {
  ok: true;
  filter: NotificationQueueFilter;
  clientId?: string;
  summary: Record<string, number>;
  canDrain: boolean;
  /** S3-12: measured on one row beyond the page, never inferred from a count. */
  hasMore?: boolean;
  cursor?: NotificationQueueCursor | null;
  items: NotificationQueueItem[];
}

/**
 * S3-12: one place builds the query, so a "load older" page carries the SAME
 * status and client as the first one - the law the timeline wrote for its own
 * cursor (a page with dropped filters silently changes what the list means).
 */
export function notificationQueueQuery(
  filter: NotificationQueueFilter,
  clientId: string,
  cursor: NotificationQueueCursor | null
): string {
  const params = new URLSearchParams();
  params.set("status", filter);
  if (clientId) params.set("clientId", clientId);
  if (cursor) {
    params.set("beforeRank", String(cursor.rank));
    params.set("beforeAt", cursor.at);
    params.set("beforeId", cursor.id);
  }
  return `/api/admin/notifications/queue?${params.toString()}`;
}

export interface NotificationDetailResponse {
  ok: true;
  notification: NotificationQueueItem;
  attempts: NotificationAttempt[];
  event: { id: string; type: string; createdAt: string; actorName: string | null; summary: Record<string, string> } | null;
}

export interface DeliveryTestResult {
  notificationId: string;
  channel: string;
  status: NotificationStatus;
  lastError: string | null;
  provider: string | null;
}

/**
 * WHO the message was for, in words a human can act on. An e-mail row already
 * carries an address; a push row carries the internal user id, and printing it
 * raw ("m6-demo-partner-18da3748-...") told the reader nothing (audit S-06).
 * The id stays reachable as the element's title, never as the label.
 */
export function notificationRecipientLabel(item: NotificationQueueItem): string {
  const name = item.recipientName?.trim();
  const email = item.recipientEmail?.trim();
  if (name && email) return `${name} · ${email}`;
  return name || email || item.recipient;
}

/**
 * The provider column printed the internal delivery mode: `test_sink` next to
 * a bare `200` under no headings at all read as data nobody could parse
 * (audit S-07). Known modes get a name, unknown ones stay verbatim rather than
 * disappear behind a guess.
 */
export const NOTIFICATION_PROVIDERS = ["test_sink", "resend", "cf_email", "web_push"] as const;

export function notificationProviderLabel(provider: string, t: (key: string) => string): string {
  return (NOTIFICATION_PROVIDERS as readonly string[]).includes(provider)
    ? t(`staffConsole.adminNotifications.provider.${provider}`)
    : provider;
}

/**
 * The reasons the sender writes into `last_error`. The panel printed the
 * column raw, so a message the recipient's own preferences had stopped read as
 * `inbox_only` in a red bar above a sentence that explained the very same fact
 * in Czech, on a row whose status was "Přeskočeno" (audit S2-07). Wave 4 wrote
 * the law for delivery modes; a reason code is the same kind of internal
 * string and gets the same treatment.
 */
export const NOTIFICATION_REASONS = [
  "inbox_only", "channel_off", "category_off", "no_subscription", "no_recipient", "unsubscribed"
] as const;

export function notificationReasonLabel(reason: string, t: (key: string) => string): string {
  return (NOTIFICATION_REASONS as readonly string[]).includes(reason)
    ? t(`staffConsole.adminNotifications.reason.${reason}`)
    : reason;
}

/**
 * A row without a stored subject used to wear its internal type as the
 * headline, so every login mail in the queue was called `return_magic_link`
 * (audit S2-07). The type is a real fact, it just needs a human name.
 *
 * 🔴 F138 kolečko 2: the dictionary moved to `./notification-presentation`,
 * which the client detail's own notification pane reads too - it used to print
 * the raw type and status, and the flattening wave put that pane on the
 * primary surface. The move also fixed the dictionary itself: seven of its
 * eight hand-typed names matched no notification this product queues. The
 * names now come from the F117 inventory, and an unknown type is NAMED rather
 * than printed.
 */
export {
  NOTIFICATION_TYPE_NAMES,
  notificationStatusLabel,
  notificationStatusTone,
  notificationTitle,
  notificationTypeHint,
  notificationTypeLabel
};

export function visibleNotificationItems(
  items: NotificationQueueItem[],
  hideTestTenants: boolean
): NotificationQueueItem[] {
  return hideTestTenants ? items.filter((item) => !item.isTest) : items;
}

/**
 * A test that came back "skipped" is not a success: it says the channel is off.
 * Neither is a test that landed in the development sink (audit S2-09): the
 * panel said "Test doručený na ... Zkontroluj schránku." and sixty pixels lower
 * drew the very row it had just made, labelled TESTOVACÍ SCHRÁNKA. This panel
 * exists to catch a message that quietly went nowhere; it may not produce one
 * and call it delivered. The fact was already in the answer (`dev_logged`,
 * provider `test_sink`), it was simply not looked at.
 */
export function deliveryTestMessageKey(results: DeliveryTestResult[]): string {
  if (results.some((result) => result.status === "failed")) return "staffConsole.adminNotifications.test.failed";
  if (results.every((result) => result.status === "skipped")) return "staffConsole.adminNotifications.test.skipped";
  if (results.some((result) => result.status === "queued")) return "staffConsole.adminNotifications.test.queued";
  if (results.every((result) => result.status === "dev_logged" || result.provider === "test_sink")) {
    return "staffConsole.adminNotifications.test.sink";
  }
  return "staffConsole.adminNotifications.test.delivered";
}

export function NotificationActionFeedback(props: {
  state: "idle" | "working" | "done" | "error";
  message: string | null;
  t: (key: string) => string;
}) {
  return (
    <div className="admin-notifications-feedback" aria-live="polite">
      {props.state === "working" && <p>{props.t("staffConsole.adminNotifications.working")}</p>}
      {props.state === "done" && props.message && <p className="success-message">{props.message}</p>}
      {props.state === "error" && (
        <p className="error-message" role="alert">{props.message ?? props.t("staffConsole.adminNotifications.error")}</p>
      )}
    </div>
  );
}

export function NotificationAttemptList(props: {
  attempts: NotificationAttempt[];
  locale: Locale;
  t: (key: string) => string;
}) {
  if (!props.attempts.length) {
    return <p className="staff-empty">{props.t("staffConsole.adminNotifications.noAttempts")}</p>;
  }
  // Four values under no headings ("07.08.26 17:02 · test_sink · 200 · bez
  // chyby") read as a code dump: nobody outside this file knows that 200 is an
  // HTTP answer and not a count. The facts list a few pixels above labels every
  // one of its values; so does this now (audit S-07).
  return (
    <table className="admin-notification-attempts">
      <thead>
        <tr>
          <th scope="col">{props.t("staffConsole.adminNotifications.attemptWhen")}</th>
          <th scope="col">{props.t("staffConsole.adminNotifications.attemptChannel")}</th>
          <th scope="col">{props.t("staffConsole.adminNotifications.attemptResponse")}</th>
          <th scope="col">{props.t("staffConsole.adminNotifications.attemptResult")}</th>
        </tr>
      </thead>
      <tbody>
        {props.attempts.map((attempt, index) => (
          <tr key={`${attempt.attemptedAt}-${index}`} className={attempt.error ? "failed" : undefined}>
            <td data-label={props.t("staffConsole.adminNotifications.attemptWhen")}>
              <time dateTime={attempt.attemptedAt}>{formatDate(attempt.attemptedAt, props.locale)}</time>
            </td>
            <td className="attempt-provider" data-label={props.t("staffConsole.adminNotifications.attemptChannel")}>
              {notificationProviderLabel(attempt.provider, props.t)}
            </td>
            <td className="attempt-status" data-label={props.t("staffConsole.adminNotifications.attemptResponse")}>
              {attempt.httpStatus === null ? props.t("staffConsole.adminNotifications.noStatus") : String(attempt.httpStatus)}
            </td>
            <td className="attempt-error" data-label={props.t("staffConsole.adminNotifications.attemptResult")}>
              {attempt.error ?? props.t("staffConsole.adminNotifications.attemptOk")}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/**
 * A queued message whose send time is still ahead is SCHEDULED, not stuck.
 * "Čeká" over "pokusů: 0" on a two day old reminder reads as a lost message,
 * and the operator retries something that was never wrong. Same law as wave 3
 * gave the import queue: a running job is not a broken one.
 */
export function notificationRowNote(
  item: NotificationQueueItem,
  now: number,
  t: (key: string) => string,
  locale: Locale
): string {
  if (item.status === "queued" && item.sendAt && Date.parse(item.sendAt) > now) {
    return t("staffConsole.adminNotifications.rowScheduled")
      .replace("{when}", formatDate(item.sendAt, locale));
  }
  return t("staffConsole.adminNotifications.attemptCount")
    .replace("{count}", String(item.attemptCount));
}

export function NotificationRow(props: {
  item: NotificationQueueItem;
  selected: boolean;
  locale: Locale;
  onSelect: () => void;
  t: (key: string) => string;
  /** Injected by the tests so the scheduled wording has a fixed clock. */
  now?: number;
}) {
  const { item, t } = props;
  return (
    <button type="button" className="admin-notification-row" aria-pressed={props.selected} onClick={props.onSelect}>
      <span className={`status-pill ${notificationStatusTone(item.status)}`}>
        {notificationStatusLabel(item.status, t)}
      </span>
      {/* The subject is what the row is FOR, so it gets the free width and one
          line to itself; who it is about and where it went are the second line
          (audit S-05). They used to be a 0.8fr column of their own, which left
          the subject 155px and clipped every message. */}
      <span className="admin-notification-subject">
        <strong title={notificationTypeHint(item.type)}>{notificationTitle(item, t)}</strong>
        <span className="admin-notification-context">
          <span className="admin-notification-client">
            {item.clientName ?? t("staffConsole.adminNotifications.noClient")}
          </span>
          {/* S-06: a human recipient; the internal id stays reachable in the
              title, never printed raw. */}
          <small title={item.recipient}>{notificationRecipientLabel(item)}</small>
        </span>
      </span>
      <span className="admin-notification-when">
        <time dateTime={item.createdAt}>{formatDate(item.createdAt, props.locale)}</time>
        <small>{notificationRowNote(item, props.now ?? Date.now(), t, props.locale)}</small>
      </span>
    </button>
  );
}

export function AdminNotifications(props: {
  t: (key: string) => string;
  locale: Locale;
  csrfToken: string;
  hideTestTenants: boolean;
  onHideTestTenantsChange?: (hide: boolean) => void;
  onOpenClient?: (clientId: string) => void;
  /** S3-12: the picker that answers "did the message to THIS client go out". */
  clients?: Array<{ id: string; name: string; email: string }>;
  /** Test and fixture seam, exactly like `initialDetail` below: production
   * never passes it, so the panel keeps fetching on mount. */
  initialQueue?: NotificationQueueResponse;
}) {
  const { t } = props;
  const [queue, setQueue] = useState<NotificationQueueResponse | null>(props.initialQueue ?? null);
  const [loadState, setLoadState] = useState<"loading" | "ready" | "error">(props.initialQueue ? "ready" : "loading");
  const [filter, setFilter] = useState<NotificationQueueFilter>("all");
  const [clientId, setClientId] = useState("");
  const [items, setItems] = useState<NotificationQueueItem[]>(props.initialQueue?.items ?? []);
  const [cursor, setCursor] = useState<NotificationQueueCursor | null>(props.initialQueue?.cursor ?? null);
  const [hasMore, setHasMore] = useState(Boolean(props.initialQueue?.hasMore));
  const [appending, setAppending] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [state, setState] = useState<"idle" | "working" | "done" | "error">("idle");
  const [message, setMessage] = useState<string | null>(null);
  // A retry or a delivery test changes rows that are already on screen. The
  // token reloads the list AND remounts the open card, so the panel never
  // shows a status the operator has just changed.
  const [refreshToken, setRefreshToken] = useState(0);

  const load = useCallback(async (
    next: NotificationQueueFilter,
    nextClientId: string,
    from: NotificationQueueCursor | null
  ) => {
    if (from) setAppending(true);
    else setLoadState((current) => (current === "ready" ? current : "loading"));
    try {
      const response = await fetch(notificationQueueQuery(next, nextClientId, from), { credentials: "include" });
      if (!response.ok) throw new Error("queue_failed");
      const body = await response.json() as NotificationQueueResponse;
      setQueue(body);
      setItems((current) => from ? [...current, ...body.items] : body.items);
      setCursor(body.cursor ?? null);
      setHasMore(Boolean(body.hasMore));
      setLoadState("ready");
    } catch {
      if (!from) setLoadState("error");
    } finally {
      setAppending(false);
    }
  }, []);

  useEffect(() => {
    if (!props.initialQueue) void load(filter, clientId, null);
  }, [filter, clientId, load, props.initialQueue]);

  const visible = visibleNotificationItems(items, props.hideTestTenants);
  const summary = queue?.summary ?? {};
  // The per-status counts come from the WHOLE table, the rows pass the global
  // test filter first. Without saying so, "Selhalo 3" over one visible row
  // reads as a lost message, and an all-test page reads as an empty queue.
  const hiddenByTestFilter = items.length - visible.length;
  // S2-05: three numbers on one screen, none of them about the same rows.
  // The chips census the whole table, the list is one page of at most forty,
  // and the filter note counted that page. So each one now says its own scope:
  // the chips are labelled as the whole queue, and the note is followed by the
  // page line whenever the queue holds more than the page shows. Nobody has to
  // guess again whether "40" means the queue or the screen.
  const loaded = items.length;
  const inFilter = filter === "all" ? Number(summary.total ?? 0) : Number(summary[filter] ?? 0);
  const truncated = loaded > 0 && inFilter > loaded;

  async function retry(notificationId: string) {
    setState("working");
    setMessage(null);
    const response = await staffMutation(`/api/admin/notifications/${notificationId}/retry`, {
      method: "POST", csrfToken: props.csrfToken, body: {}
    });
    if (!response.ok) {
      setState("error");
      setMessage(t(staffMutationErrorKey(response, response.status === 409
        ? "staffConsole.adminNotifications.retryConflict"
        : "staffConsole.adminNotifications.error")));
      return;
    }
    setState("done");
    setMessage(t("staffConsole.adminNotifications.done.retried"));
    setRefreshToken((token) => token + 1);
    await load(filter, clientId, null);
  }

  async function drain() {
    setState("working");
    setMessage(null);
    const response = await staffMutation("/api/admin/notifications/drain", {
      method: "POST", csrfToken: props.csrfToken, body: {}
    });
    if (!response.ok) {
      setState("error");
      setMessage(t(staffMutationErrorKey(response, "staffConsole.adminNotifications.error")));
      return;
    }
    const body = await response.json() as { processed?: number };
    setState("done");
    setMessage(t("staffConsole.adminNotifications.done.drained").replace("{count}", String(body.processed ?? 0)));
    setRefreshToken((token) => token + 1);
    await load(filter, clientId, null);
  }

  return (
    // F138d: the panel's summary carries the name and the sentence; the drain
    // control is an ACTION on the queue below and stays here.
    <section className="admin-notifications">
      <div className="admin-notifications-head">
        {queue?.canDrain && (
          <div className="admin-notifications-drain">
            <button className="secondary-button compact-button" type="button"
              disabled={state === "working" || !summary.queued} onClick={() => void drain()}>
              {`${t("staffConsole.adminNotifications.drain")} (${summary.queued ?? 0})`}
            </button>
            <p className="admin-tool-hint">{t("staffConsole.notificationDrainHint")}</p>
          </div>
        )}
      </div>

      <p className="panel-label admin-notification-filters-label">
        {t("staffConsole.adminNotifications.queueScope")}
      </p>
      <div className="admin-notification-filters" role="group" aria-label={t("staffConsole.adminNotifications.filterLabel")}>
        {NOTIFICATION_QUEUE_FILTERS.map((value) => (
          <button key={value} type="button" aria-pressed={filter === value}
            className={value === "all" ? "notification-filter" : `notification-filter ${notificationStatusTone(value as NotificationStatus)}`}
            onClick={() => { setFilter(value); setSelectedId(null); }}>
            {t(`staffConsole.adminNotifications.filter.${value}`)}
            <span className="filter-count">
              {String(value === "all" ? (summary.total ?? 0) : (summary[value] ?? 0))}
            </span>
          </button>
        ))}
      </div>
      {/* 🔴 S3-12: the panel exists to answer "did the message to client X go
          out", and it could only answer it while that message was among the
          newest forty of nearly five thousand. The picker asks the server, so
          the chips above it count the same client, and "Načíst starší" below
          reaches past the page - the same two moves the timeline already has. */}
      {props.clients && props.clients.length > 0 && (
        <label className="inline-select admin-notification-client-filter">
          <span>{t("staffConsole.adminNotifications.filterClient")}</span>
          <select value={clientId} onChange={(event) => {
            setClientId(event.target.value);
            setSelectedId(null);
          }}>
            <option value="">{t("staffConsole.adminNotifications.allClients")}</option>
            {clientPickerOptions(props.clients).map((option) => (
              <option key={option.id} value={option.id}>{clientPickerLabel(option, t)}</option>
            ))}
          </select>
        </label>
      )}
      {Number(summary.scheduled ?? 0) > 0 && (
        <p className="admin-tool-hint">
          {t("staffConsole.adminNotifications.scheduled").replace("{count}", String(summary.scheduled))}
        </p>
      )}

      <DeliveryTestCard t={t} csrfToken={props.csrfToken}
        onSent={() => { setRefreshToken((token) => token + 1); void load(filter, clientId, null); }} />

      <div className="admin-notifications-split">
        <div className="admin-notification-list">
          {loadState === "loading" && <p>{t("common.loading")}</p>}
          {loadState === "error" && (
            <div>
              <p className="error-message" role="alert">{t("staffConsole.adminNotifications.loadError")}</p>
              <button className="secondary-button compact-button" type="button" onClick={() => void load(filter, clientId, null)}>
                {t("common.retry")}
              </button>
            </div>
          )}
          {loadState === "ready" && !visible.length && (
            <p className="staff-empty">
              {t(hiddenByTestFilter > 0
                ? "staffConsole.adminNotifications.allFiltered"
                : "staffConsole.adminNotifications.empty")}
            </p>
          )}
          {visible.map((item) => (
            <NotificationRow key={item.id} item={item} locale={props.locale} t={t}
              selected={item.id === selectedId} onSelect={() => setSelectedId(item.id)} />
          ))}
          <TestFilterNote shown={visible.length} hidden={hiddenByTestFilter} t={t}
            onShowTest={props.onHideTestTenantsChange && (() => props.onHideTestTenantsChange?.(false))} />
          {truncated && (
            <p className="admin-tool-hint admin-notification-page-note">
              {t("staffConsole.adminNotifications.pageNote")
                .replace("{loaded}", String(loaded))
                .replace("{total}", String(inFilter))}
            </p>
          )}
          {hasMore && (
            <button className="secondary-button admin-notification-more" type="button" disabled={appending}
              onClick={() => void load(filter, clientId, cursor)}>
              {t(appending
                ? "staffConsole.adminNotifications.loadingMore"
                : "staffConsole.adminNotifications.loadMore")}
            </button>
          )}
        </div>
        <div className="admin-notification-detail">
          {selectedId
            ? (
              <NotificationDetail key={`${selectedId}:${refreshToken}`} notificationId={selectedId}
                locale={props.locale} t={t} busy={state === "working"} onRetry={retry}
                onOpenClient={props.onOpenClient} />
            )
            : <p className="staff-empty">{t("staffConsole.adminNotifications.selectRow")}</p>}
        </div>
      </div>

      <NotificationActionFeedback state={state} message={message} t={t} />
    </section>
  );
}

export function NotificationDetail(props: {
  notificationId: string;
  locale: Locale;
  t: (key: string) => string;
  busy: boolean;
  onRetry: (notificationId: string) => Promise<void>;
  onOpenClient?: (clientId: string) => void;
  initialDetail?: NotificationDetailResponse;
}) {
  const { t } = props;
  const [detail, setDetail] = useState<NotificationDetailResponse | null>(props.initialDetail ?? null);
  const [loadState, setLoadState] = useState<"loading" | "ready" | "error">(props.initialDetail ? "ready" : "loading");

  const load = useCallback(async () => {
    try {
      const response = await fetch(`/api/admin/notifications/${props.notificationId}/detail`, { credentials: "include" });
      if (!response.ok) throw new Error("detail_failed");
      setDetail(await response.json() as NotificationDetailResponse);
      setLoadState("ready");
    } catch {
      setLoadState("error");
    }
  }, [props.notificationId]);

  useEffect(() => { if (!props.initialDetail) void load(); }, [load, props.initialDetail]);

  if (loadState === "loading") return <p>{t("common.loading")}</p>;
  if (loadState === "error" || !detail) {
    return (
      <div>
        <p className="error-message" role="alert">{t("staffConsole.adminNotifications.loadError")}</p>
        <button className="secondary-button compact-button" type="button" onClick={() => void load()}>
          {t("common.retry")}
        </button>
      </div>
    );
  }

  const item = detail.notification;
  return (
    <article className="admin-notification-card">
      <header>
        <span className={`status-pill ${notificationStatusTone(item.status)}`}>
          {notificationStatusLabel(item.status, t)}
        </span>
        <h4 title={notificationTypeHint(item.type)}>{notificationTitle(item, t)}</h4>
        <p className="admin-notification-recipient" title={item.recipient}>
          {`${t("staffConsole.adminNotifications.recipientLabel")}: ${notificationRecipientLabel(item)}`}
        </p>
      </header>

      <ul className="admin-notification-facts">
        <li>
          <span>{t("staffConsole.adminNotifications.channel")}</span>
          <strong>{t(`staffConsole.adminNotifications.channelName.${item.channel}`)}</strong>
        </li>
        <li>
          <span>{t("staffConsole.adminNotifications.created")}</span>
          <strong>{formatDate(item.createdAt, props.locale)}</strong>
        </li>
        <li>
          <span>{t("staffConsole.adminNotifications.attempts")}</span>
          <strong>{String(item.attemptCount)}</strong>
        </li>
        {item.sendAt && (
          <li>
            <span>{t("staffConsole.adminNotifications.sendAt")}</span>
            <strong>{formatDate(item.sendAt, props.locale)}</strong>
          </li>
        )}
        {item.sentAt && (
          <li>
            <span>{t("staffConsole.adminNotifications.sentAt")}</span>
            <strong>{formatDate(item.sentAt, props.locale)}</strong>
          </li>
        )}
      </ul>

      {/* S2-07: a named reason, and red only when the message really failed.
          A "Přeskočeno" row is not an error, and printing `inbox_only` in a
          red bar above the sentence that explains it made a preference look
          like a fault. */}
      {item.lastError && (
        <p className={item.status === "failed" ? "error-message" : "admin-tool-hint"}>
          {`${t("staffConsole.adminNotifications.reasonLabel")}: ${notificationReasonLabel(item.lastError, t)}`}
        </p>
      )}

      <section className="admin-notification-block">
        <p className="panel-label">{t("staffConsole.adminNotifications.attemptsTitle")}</p>
        <NotificationAttemptList attempts={detail.attempts} locale={props.locale} t={t} />
      </section>

      <section className="admin-notification-block">
        <p className="panel-label">{t("staffConsole.adminNotifications.eventTitle")}</p>
        {/* "Vyvolala událost" over a bare "06.08.26 13:47" answered its own
            heading with a timestamp: the payload carried `type` and the block
            threw it away (S2-07). It is named with the SAME presenter the
            timeline uses, so one event never reads two ways. */}
        {detail.event
          ? (
            <p className="admin-notification-event">
              <strong>{adminActivityPresentation({
                id: detail.event.id,
                type: detail.event.type,
                createdAt: detail.event.createdAt,
                clientId: item.clientId,
                clientName: item.clientName,
                actorName: detail.event.actorName,
                summary: detail.event.summary
              } as AdminActivityEntry, t).title}</strong>
              <span>
                {[detail.event.actorName, formatDate(detail.event.createdAt, props.locale)]
                  .filter(Boolean).join(" · ")}
              </span>
            </p>
          )
          : <p className="staff-empty">{t("staffConsole.adminNotifications.eventMissing")}</p>}
        {item.clientId && (
          // S3-07: a word inside a card keeps its own small shape, but it may
          // not be a path that throws the whole console away on a plain click.
          <a className="activity-client-link" href={`/?client=${encodeURIComponent(item.clientId)}`}
            onClick={openClientHandler(item.clientId, props.onOpenClient)}>
            {t("staffConsole.activity.openClient")}
          </a>
        )}
      </section>

      {item.status === "failed" && (
        <div className="admin-notification-retry">
          <button className="primary-button" type="button" disabled={props.busy}
            onClick={() => void props.onRetry(item.id)}>
            {t("staffConsole.adminNotifications.retry")}
          </button>
          <p className="admin-tool-hint">{t("staffConsole.notificationRetryHint")}</p>
        </div>
      )}
      {/* Only when the reason line above did NOT already say it: with the
          code translated, "Důvod: Příjemce má nastavené jen upozornění v
          aplikaci." and this hint were the same sentence twice. */}
      {item.status === "skipped" && !item.lastError && (
        <p className="admin-tool-hint">{t("staffConsole.adminNotifications.skippedHint")}</p>
      )}
    </article>
  );
}

export function DeliveryTestCard(props: {
  t: (key: string) => string;
  csrfToken: string;
  onSent: () => void;
}) {
  const { t } = props;
  const [channel, setChannel] = useState("email");
  const [state, setState] = useState<"idle" | "working" | "done" | "error">("idle");
  const [message, setMessage] = useState<string | null>(null);

  async function send() {
    setState("working");
    setMessage(null);
    const response = await staffMutation("/api/admin/notifications/delivery-test", {
      method: "POST", csrfToken: props.csrfToken, body: { channel }
    });
    if (!response.ok) {
      setState("error");
      setMessage(t(staffMutationErrorKey(response, "staffConsole.adminNotifications.error")));
      return;
    }
    const body = await response.json() as { recipient: string; results: DeliveryTestResult[] };
    setState("done");
    setMessage(t(deliveryTestMessageKey(body.results)).replace("{recipient}", body.recipient));
    props.onSent();
  }

  return (
    <section className="admin-notification-test">
      <div>
        <p className="panel-label">{t("staffConsole.adminNotifications.test.kicker")}</p>
        <p>{t("staffConsole.adminNotifications.test.hint")}</p>
      </div>
      <div className="admin-notification-actions">
        <label className="inline-select">
          <span>{t("staffConsole.adminNotifications.channel")}</span>
          <select value={channel} onChange={(event) => setChannel(event.target.value)}>
            <option value="email">{t("staffConsole.adminNotifications.channelName.email")}</option>
            <option value="push">{t("staffConsole.adminNotifications.channelName.push")}</option>
            <option value="both">{t("staffConsole.adminNotifications.channelName.both")}</option>
          </select>
        </label>
        <button className="secondary-button" type="button" disabled={state === "working"} onClick={() => void send()}>
          {t("staffConsole.adminNotifications.test.send")}
        </button>
      </div>
      <NotificationActionFeedback state={state} message={message} t={t} />
    </section>
  );
}
